import { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  ExternalLinkIcon,
  Loader2Icon,
  PenLineIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { Address } from "viem";
import {
  formatEth,
  gasText,
  lowOnGas,
  submitterFunding,
  type ExchangedEvent,
  type SpentEvent,
} from "@woodpecker/chain";
import { Button, Badge } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import { Input, Label } from "../ui/form";
import { perCredit, short, shortTx, wpOf } from "../lib/format";
import { ServiceIcon } from "../lib/icons";
import { useN, useT, useTT, Y } from "../i18n";
import { useStore } from "../store/store";
import { useWallet } from "../store/wallet";
import { pricingHash } from "@woodpecker/protocol";
import type { ServiceEntry } from "../adapters/chain";
import { PriceCommitmentText } from "./Services";
import { BatchLine, Kinds, Labels, PriceLine, Rejected, Waiting } from "../components/common";
import { GAS, useSignLines } from "../components/signing";
import { navigate } from "../components/Shell";

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function SignState({ state, n }: { state: "idle" | "waiting" | "rejected"; n: number }) {
  return state === "waiting" ? (
    <Waiting n={n} className="text-xs" />
  ) : state === "rejected" ? (
    <Rejected className="text-xs" />
  ) : null;
}

function TokenCard({ entry }: { entry: ServiceEntry }) {
  const t = useT();
  const n = useN();
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("manage.token.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl>
          <Row label={t("manage.token.symbol")}>
            <span className="font-mono">{entry.token}</span>
          </Row>
          <Row label={t("manage.token.address")}>
            <span className="font-mono">{entry.tokenAddress}</span>
          </Row>
          <Row label={t("manage.token.owner")}>
            <span className="font-mono">{short(entry.owner)}</span>{" "}
            <span className="text-muted-foreground">{t("manage.token.thisWallet")}</span>
          </Row>
          <Row label="service()">
            <span className="font-mono">{short(entry.submitter)}</span>{" "}
            <span className="text-muted-foreground">= submitter</span>
          </Row>
          <Row label={t("manage.token.supply")}>
            <span className="tabular-nums">{n(Number(entry.info.totalSupply))}</span>
          </Row>
          <Row label={t("manage.token.decimals")}>{entry.info.decimals}</Row>
        </dl>
      </CardContent>
    </Card>
  );
}

function RateCard({ entry }: { entry: ServiceEntry }) {
  const { refreshDirectory } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const [draft, setDraft] = useState("");
  const [state, setState] = useState<"idle" | "waiting" | "rejected">("idle");
  const current = perCredit(entry.rate);
  const v = Number(draft);
  const valid = draft.trim() !== "" && v > 0 && v <= 1e6;
  function change() {
    if (!valid) return;
    setState("waiting");
    request({
      title: Y("wallet.req.setRate", { token: entry.token }),
      lines: [lines.setRate(entry.token, v)],
      onDone: async (r) => {
        if (r !== "signed") return setState("rejected");
        setState("idle");
        setDraft("");
        await refreshDirectory();
        toast.success(t("manage.rate.toast", { n: v, token: entry.token }));
      },
    });
  }
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("manage.rate.title")}</CardTitle>
        <CardDescription>{t("manage.rate.description", { token: entry.token })}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <dl>
          <Row label={t("manage.rate.current")}>
            <span className="font-mono">{t("rate.line", { n: current, token: entry.token })}</span>{" "}
            <span className="text-muted-foreground">· {t("manage.rate.callValue", { v: entry.rate.toString() })}</span>
          </Row>
        </dl>
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="rate-new">{t("manage.rate.new")}</Label>
            <div className="flex items-center gap-1.5 font-mono text-sm">
              <span>1 wpCredit =</span>
              <Input
                id="rate-new"
                type="number"
                min={0}
                step="any"
                className="w-24"
                placeholder={String(current)}
                value={draft}
                disabled={state === "waiting"}
                aria-invalid={draft.trim() !== "" && !valid}
                onChange={(e) => setDraft(e.target.value)}
              />
              <span>{entry.token}</span>
            </div>
          </div>
          <Button variant="outline" onClick={change} disabled={!valid || state === "waiting"}>
            {state === "waiting" && <Loader2Icon className="animate-spin" />}
            {t("manage.rate.change")}
          </Button>
        </div>
        {draft.trim() !== "" && !valid && <p className="text-xs text-destructive">{t("manage.rate.invalid")}</p>}
        <SignState state={state} n={1} />
      </CardContent>
    </Card>
  );
}

function SubmitterCard({ entry }: { entry: ServiceEntry }) {
  const { chain } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const [amount, setAmount] = useState("0.0001");
  const [touched, setTouched] = useState(false);
  const [gas, setGas] = useState<{ balance: bigint; gasPrice: bigint } | null>(null);
  const [state, setState] = useState<"idle" | "waiting" | "rejected">("idle");
  // the balance and the chain's gas price: the settles the submitter pays, a warning below 10, and the amount the
  // deploy card would send (100 settles, at least 0.0001 ETH) as the default of the field
  const load = () =>
    Promise.all([chain.ethBalance(entry.submitter), chain.gasPrice()])
      .then(([balance, gasPrice]) => {
        setGas({ balance, gasPrice });
        if (!touched) setAmount(formatEth(submitterFunding(gasPrice)));
      })
      .catch(() => setGas(null));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.submitter]);
  const low = gas ? lowOnGas(gas.balance, gas.gasPrice) : false;
  const valid = Number.isFinite(Number(amount)) && Number(amount) > 0;
  function fund() {
    if (!valid) return;
    setState("waiting");
    request({
      title: Y("wallet.req.fund", { name: entry.name }),
      note: Y("wallet.req.fundNote", { amount }),
      lines: [lines.sendEth(entry.submitter, amount, entry.name)],
      onDone: async (r) => {
        setState(r === "signed" ? "idle" : "rejected");
        if (r === "signed") await load();
      },
    });
  }
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("manage.submitter.title")}</CardTitle>
        <CardDescription>{t("manage.submitter.description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <dl>
          <Row label={t("manage.submitter.address")}>
            <span className="font-mono">{entry.submitter}</span>
          </Row>
          <Row label={t("manage.submitter.gas")}>
            <span className="font-mono tabular-nums">{gas === null ? "—" : gasText(gas.balance, gas.gasPrice)}</span>
          </Row>
        </dl>
        {low && <p className="text-xs text-amber-700 dark:text-amber-400">{t("manage.submitter.low")}</p>}
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1.5">
            <Label htmlFor="fund-amount" className="text-xs font-normal text-muted-foreground">
              {t("manage.submitter.amount")}
            </Label>
            <Input
              id="fund-amount"
              className="w-32 font-mono"
              inputMode="decimal"
              value={amount}
              disabled={state === "waiting"}
              onChange={(e) => {
                setTouched(true);
                setAmount(e.target.value);
              }}
            />
          </div>
          <Button onClick={fund} disabled={state === "waiting" || !valid}>
            {state === "waiting" ? <Loader2Icon className="animate-spin" /> : <PenLineIcon />}
            {t("manage.submitter.fund")}
          </Button>
        </div>
        <SignState state={state} n={1} />
      </CardContent>
    </Card>
  );
}

function VerifyCard({ entry }: { entry: ServiceEntry }) {
  const { conversations, runner, refreshConversations } = useStore();
  const t = useT();
  const deployment = conversations.find((c) => c.kind === "deploy" && c.deploy?.serviceName === entry.name);
  const sig = deployment?.messages.filter((m) => m.role === "signature" && m.id === "sig-verify").at(-1);
  const last = sig?.resultText;
  const [busy, setBusy] = useState(false);
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("manage.verify.title")}</CardTitle>
        <CardDescription>{t("manage.verify.description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheckIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
            {last
              ? t("manage.verify.last", {
                  result: last.replace(/ pass$/, ""),
                  date: deployment?.lastActivity.slice(0, 16).replace("T", " ") ?? "",
                })
              : t("manage.verify.none")}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!deployment || !runner || busy || deployment.status === "running"}
            onClick={async () => {
              if (!deployment || !runner) return;
              setBusy(true);
              try {
                await runner.reverify(deployment.id);
                await refreshConversations();
                toast(t("manage.verify.started"));
                navigate(`/conversations/${deployment.id}`);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Loader2Icon className="animate-spin" /> : <RefreshCwIcon />}
            {t("manage.verify.again")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatsCard({ entry }: { entry: ServiceEntry }) {
  const { chain, wallet } = useStore();
  const t = useT();
  const tt = useTT();
  const [spent, setSpent] = useState<SpentEvent[]>([]);
  const [exchanged, setExchanged] = useState<ExchangedEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [updated, setUpdated] = useState<string | null>(null);
  async function load() {
    setBusy(true);
    try {
      setSpent(await chain.spentEvents({ token: entry.tokenAddress }));
      setExchanged(await chain.exchangedEvents(entry.tokenAddress));
      setUpdated(new Date().toTimeString().slice(0, 5));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.tokenAddress]);
  const payers = new Map<string, { payments: number; paid: number }>();
  for (const e of spent) {
    const p = payers.get(e.client) ?? { payments: 0, paid: 0 };
    p.payments += 1;
    p.paid += Number(e.actual);
    payers.set(e.client, p);
  }
  const credits = spent.reduce((a, e) => a + e.credits, 0n) + exchanged.reduce((a, e) => a + e.credits, 0n);
  const mixed = spent.filter((e) => e.credits > 0n).length;
  return (
    <Card size="sm" className="lg:col-span-2">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("manage.stats.title")}</CardTitle>
            <CardDescription>
              {updated ? t("manage.stats.updated", { t: updated }) : t("manage.stats.description")}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
            {busy ? <Loader2Icon className="animate-spin" /> : <RefreshCwIcon />}
            {t("manage.stats.refresh")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        {(exchanged.length > 0 || mixed > 0) && (
          <p className="text-sm lg:col-span-2">
            {t("manage.stats.exchanges", { credits: wpOf(credits), n: exchanged.length, m: mixed })}
          </p>
        )}
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-medium">{t("manage.stats.perPayer")}</h3>
          <Table className="text-xs">
            <THead>
              <TR>
                <TH>{t("manage.col.wallet")}</TH>
                <TH className="text-right">{t("manage.col.payments")}</TH>
                <TH className="text-right">{t("manage.col.paid")}</TH>
              </TR>
            </THead>
            <TBody>
              {[...payers].map(([w, p]) => (
                <TR key={w}>
                  <TD>
                    <span className="font-mono">{short(w)}</span>
                    {wallet && w.toLowerCase() === wallet.address.toLowerCase() && (
                      <div className="text-[11px] text-muted-foreground">{t("manage.mint.thisWallet")}</div>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums">{p.payments}</TD>
                  <TD className="text-right tabular-nums">
                    {p.paid} {entry.token}
                  </TD>
                </TR>
              ))}
              {payers.size === 0 && (
                <TR>
                  <TD colSpan={3} className="py-4 text-center text-muted-foreground">
                    {t("manage.stats.empty")}
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </div>
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-medium">{t("manage.stats.perCall")}</h3>
          <Table className="text-xs">
            <THead>
              <TR>
                <TH>{t("manage.col.wallet")}</TH>
                <TH className="text-right">{t("col.actual")}</TH>
                <TH className="text-right">{t("col.paid")}</TH>
                <TH>{t("col.tx")}</TH>
              </TR>
            </THead>
            <TBody>
              {[...spent].reverse().map((e) => (
                <TR key={e.nonce}>
                  <TD className="font-mono">{short(e.identity)}</TD>
                  <TD className="text-right tabular-nums">{Number(e.actual)}</TD>
                  <TD className="text-right tabular-nums">{e.credits > 0n ? `${wpOf(e.credits)} wpCredit` : "—"}</TD>
                  <TD className="font-mono text-muted-foreground">{tt(shortTx(e.tx))}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function MintCard({ entry }: { entry: ServiceEntry }) {
  const { wallet, refreshDirectory, refreshBalances } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("10");
  const [state, setState] = useState<"idle" | "waiting" | "rejected">("idle");
  const [history, setHistory] = useState<Array<{ time: string; to: string; amount: number; tx: string }>>(() => {
    try {
      return JSON.parse(localStorage.getItem(`woodpecker-mints-${entry.tokenAddress}`) ?? "[]") as Array<{
        time: string;
        to: string;
        amount: number;
        tx: string;
      }>;
    } catch {
      return [];
    }
  });
  const n = Math.floor(Number(amount));
  const valid = /^0x[0-9a-fA-F]{40}$/.test(to.trim()) && n > 0;
  const self = wallet && to.trim().toLowerCase() === wallet.address.toLowerCase();
  function mint() {
    if (!valid) return;
    const target = to.trim() as Address;
    setState("waiting");
    request({
      title: self
        ? Y("wallet.req.mintSelf", { amount: n, symbol: entry.token })
        : Y("wallet.req.mintTo", { amount: n, symbol: entry.token, to: short(target) }),
      lines: [
        lines.mint(
          entry.token,
          target,
          n,
          self
            ? Y("wallet.req.mintSelf", { amount: n, symbol: entry.token })
            : Y("wallet.req.mintTo", { amount: n, symbol: entry.token, to: short(target) }),
        ),
      ],
      onDone: async (r, ls) => {
        setState(r === "signed" ? "idle" : "rejected");
        if (r !== "signed") return;
        const row = { time: new Date().toISOString(), to: target, amount: n, tx: ls[0]?.tx ?? "" };
        const next = [row, ...history];
        setHistory(next);
        try {
          localStorage.setItem(`woodpecker-mints-${entry.tokenAddress}`, JSON.stringify(next));
        } catch {
          /* no storage */
        }
        toast.success(t("manage.mint.minted", { amount: n, symbol: entry.token, to: short(target) }));
        await refreshDirectory();
        await refreshBalances();
      },
    });
  }
  return (
    <Card size="sm" className="lg:col-span-2">
      <CardHeader>
        <CardTitle>{t("manage.mint.title")}</CardTitle>
        <CardDescription>{t("manage.mint.description")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs font-normal text-muted-foreground">{t("manage.mint.token")}</Label>
            <span className="font-mono text-sm">{entry.token}</span>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mint-to" className="text-xs font-normal text-muted-foreground">
              {t("manage.mint.to")}
            </Label>
            <Input
              id="mint-to"
              className="font-mono"
              placeholder="0x…"
              value={to}
              disabled={state === "waiting"}
              onChange={(e) => setTo(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t("manage.mint.book")}</span>
              {wallet && (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setTo(wallet.address)}
                  disabled={state === "waiting"}
                >
                  {t("manage.mint.thisWallet")} <span className="font-mono">{short(wallet.address)}</span>
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="mint-amount" className="text-xs font-normal text-muted-foreground">
                {t("manage.mint.amount")}
              </Label>
              <Input
                id="mint-amount"
                type="number"
                min={1}
                step={1}
                className="w-28"
                value={amount}
                disabled={state === "waiting"}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <Button onClick={mint} disabled={state === "waiting" || !valid}>
              {state === "waiting" ? <Loader2Icon className="animate-spin" /> : <PenLineIcon />}
              {t("manage.mint.button")}
            </Button>
          </div>
          <SignState state={state} n={1} />
        </div>
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-medium">{t("manage.mint.history")}</h3>
          <Table className="text-xs">
            <THead>
              <TR>
                <TH>{t("col.time")}</TH>
                <TH>{t("manage.col.to")}</TH>
                <TH className="text-right">{t("manage.col.amount")}</TH>
                <TH>{t("col.tx")}</TH>
              </TR>
            </THead>
            <TBody>
              {history.map((h, i) => (
                <TR key={i}>
                  <TD>{h.time.slice(0, 16).replace("T", " ")}</TD>
                  <TD className="font-mono">{short(h.to)}</TD>
                  <TD className="text-right tabular-nums">
                    {h.amount} {entry.token}
                  </TD>
                  <TD className="font-mono text-muted-foreground">{shortTx(h.tx)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The entry does not commit the hash of the document the service serves now (a redeploy changed it, or nothing was
 * committed): one wallet signature commits it, so runners accept the service again (issue #3 item 11, option A).
 */
function CommitPriceHash({ entry }: { entry: ServiceEntry }) {
  const { refreshDirectory, config, chain, wallet } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const [busy, setBusy] = useState(false);
  const doc = entry.doc;
  const commit = () => {
    if (!doc || !wallet) return;
    const hash = pricingHash(doc);
    setBusy(true);
    request({
      title: Y("wallet.req.commitPrice", { name: entry.name }),
      note: Y("wallet.req.nTransactions", { n: 1 }),
      lines: [
        lines.raw(
          {
            plain: Y("wallet.plain.setPricingHash", { name: entry.name }),
            text: `ServiceDirectory.setPricingHash(${entry.token}, ${hash})`,
            address: config.directory,
            gas: GAS.register,
          },
          async () => ({ tx: (await chain.setPricingHash(wallet.address, entry.tokenAddress, hash)).tx }),
        ),
      ],
      onDone: async (r) => {
        setBusy(false);
        if (r === "signed") {
          toast.success(t("manage.price.committed", { name: entry.name }));
          await refreshDirectory();
        }
      },
    });
  };
  if (!wallet) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <p className="text-xs text-amber-700 dark:text-amber-400">{t("manage.price.hint")}</p>
      <Button size="sm" variant="outline" disabled={!doc || busy} onClick={commit}>
        {busy ? <Loader2Icon className="animate-spin" /> : <ShieldCheckIcon />}
        {t("manage.price.commit")}
      </Button>
    </div>
  );
}

export function ManagePage({ name }: { name: string }) {
  const { directory, chain, wallet, refreshDirectory } = useStore();
  const t = useT();
  const entry = directory.find((e) => e.name === name);
  const back = (
    <a
      href="#/services"
      className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeftIcon className="size-4" />
      {t("deploy.back")}
    </a>
  );
  if (!entry || !entry.mine) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <p className="py-12 text-center text-muted-foreground">{t("manage.notMine")}</p>
      </div>
    );
  }
  async function retire() {
    if (!wallet || !window.confirm(t("manage.retireConfirm", { name: entry!.name }))) return;
    try {
      await chain.clear(wallet.address, entry!.tokenAddress);
      toast.success(t("manage.retired", { name: entry!.name }));
      await refreshDirectory();
      navigate("/services");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <>
      <div className="mb-4 flex flex-col gap-2">
        {back}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="flex items-center gap-2.5 font-mono text-2xl font-semibold tracking-tight">
            <ServiceIcon entry={entry} />
            {entry.name}
          </h1>
          <Kinds kinds={entry.kinds} />
          {entry.ready && (
            <Badge variant="outline" className="text-emerald-700 dark:text-emerald-300">
              {t("manage.verified")}
            </Badge>
          )}
          {entry.reachable && (
            <Badge variant="outline">
              <PriceCommitmentText entry={entry} />
            </Badge>
          )}
          <Labels entry={entry} />
        </div>
        {entry.reachable && entry.priceCommitment !== "committed" && <CommitPriceHash entry={entry} />}
        <span className="flex flex-wrap items-baseline gap-x-1.5">
          <PriceLine entry={entry} />
          <BatchLine entry={entry} />
        </span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
          <span className="font-mono text-xs break-all">{entry.origin}</span>
          <a
            className="inline-flex items-center gap-1 underline"
            href={`${entry.origin}/pricing`}
            target="_blank"
            rel="noreferrer"
          >
            {t("manage.openPricing")}
            <ExternalLinkIcon className="size-3.5" />
          </a>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => void retire()}>
            {t("manage.retire")}
          </Button>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <TokenCard entry={entry} />
        <RateCard entry={entry} />
        <SubmitterCard entry={entry} />
        <VerifyCard entry={entry} />
        <StatsCard entry={entry} />
        <MintCard entry={entry} />
      </div>
    </>
  );
}
